import type { Descriptor, HttpMethod, Json, Route, ToolError } from "./index";

// A trailing `{name+}` is greedy: it takes the rest of the path, slashes
// included, as an S3 key does (API Gateway spells it the same way).
const PATH =
  /^\/api\/(?:[a-z0-9_]+|\{[a-z][a-zA-Z0-9]*\})(?:\/(?:[a-z0-9_]+|\{[a-z][a-zA-Z0-9]*\}))*(?:\/\{[a-z][a-zA-Z0-9]*\+\})?$/;
/** The adapter's own status route. */
const RESERVED = /^\/api\/invocations(?:\/|$)/;

export function splitRoute(route: Route): { method: HttpMethod; path: string } {
  const space = route.indexOf(" ");
  return { method: route.slice(0, space) as HttpMethod, path: route.slice(space + 1) };
}

export function pathParams(path: string): string[] {
  return [...path.matchAll(/\{([a-z][a-zA-Z0-9]*)\+?\}/g)].map((match) => match[1]!);
}

export function honoPath(path: string): string {
  return path
    .replace(/\{([a-z][a-zA-Z0-9]*)\+\}/g, ":$1{.+}")
    .replace(/\{([a-z][a-zA-Z0-9]*)\}/g, ":$1");
}

export function matchPath(template: string, pathname: string): Record<string, string> | null {
  const names = pathParams(template);
  const pattern = new RegExp(
    `^${template.replace(/\{[a-z][a-zA-Z0-9]*\+\}/g, "(.+)").replace(/\{[a-z][a-zA-Z0-9]*\}/g, "([^/]+)")}$`,
  );
  const match = pattern.exec(pathname);
  if (!match) return null;
  return Object.fromEntries(
    names.map((name, index) => [name, decodeURIComponent(match[index + 1]!)]),
  );
}

function schemaTypes(schema: unknown): string[] {
  if (!schema || typeof schema !== "object") return [];
  const value = schema as { type?: unknown; anyOf?: unknown[]; oneOf?: unknown[] };
  if (typeof value.type === "string") return value.type === "null" ? [] : [value.type];
  if (Array.isArray(value.type))
    return value.type.filter((type): type is string => type !== "null");
  const alternatives = value.anyOf ?? value.oneOf;
  if (Array.isArray(alternatives)) return alternatives.flatMap(schemaTypes);
  return [];
}

export function queryCoercible(schema: unknown): boolean {
  const types = schemaTypes(schema);
  return (
    types.length > 0 &&
    types.every((type) => ["string", "integer", "number", "boolean"].includes(type))
  );
}

export function coerceQueryValue(
  schema: unknown,
  raw: string,
): { ok: true; value: Json } | { ok: false; message: string } {
  const types = schemaTypes(schema);
  if (types.includes("integer") && /^-?\d+$/.test(raw)) return { ok: true, value: Number(raw) };
  if (types.includes("number") && /^-?(?:\d+|\d+\.\d+|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw)) {
    const value = Number(raw);
    if (Number.isFinite(value)) return { ok: true, value };
  }
  if (types.includes("boolean") && (raw === "true" || raw === "false"))
    return { ok: true, value: raw === "true" };
  if (types.includes("string")) return { ok: true, value: raw };
  return {
    ok: false,
    message: `Cannot coerce ${JSON.stringify(raw)} to ${types.join("|") || "a query value"}`,
  };
}

export function lookupInputProperty(
  input: Record<string, unknown>,
  name: string,
): Record<string, unknown> | undefined {
  const properties = input.properties as Record<string, Record<string, unknown>> | undefined;
  return properties?.[name];
}

export function validateRoute(descriptor: {
  route: Route;
  effect: Descriptor["effect"];
  input: Record<string, unknown>;
}): void {
  const { route, input } = descriptor;
  const http = splitRoute(route);
  if (!PATH.test(http.path))
    throw new Error(
      `${route}: HTTP path must be /api/ plus lowercase, snake_case or {property} segments, optionally ending in one greedy {property+}`,
    );
  if (RESERVED.test(http.path))
    throw new Error(`${route}: HTTP path collides with /api/invocations`);
  if (descriptor.effect !== "read" && http.method === "GET")
    throw new Error(`${route}: a write cannot be a GET`);
  if (descriptor.effect === "read" && http.method === "DELETE")
    throw new Error(`${route}: a DELETE must be a write`);
  if (!["GET", "POST", "DELETE"].includes(http.method))
    throw new Error(`${route}: HTTP method must be GET, POST or DELETE`);
  const required = new Set(
    Array.isArray(input.required)
      ? input.required.filter((name): name is string => typeof name === "string")
      : [],
  );
  const seen = new Set<string>();
  for (const name of pathParams(http.path)) {
    if (seen.has(name)) throw new Error(`${route}: duplicate path parameter {${name}}`);
    seen.add(name);
    const property = lookupInputProperty(input, name);
    if (!property) throw new Error(`${route}: path parameter {${name}} is not an input property`);
    if (!required.has(name)) throw new Error(`${route}: path parameter {${name}} must be required`);
    if (
      !schemaTypes(property).every((type) => type === "string") ||
      schemaTypes(property).length === 0
    )
      throw new Error(`${route}: path parameter {${name}} must be a string`);
  }
  const remaining = Object.keys(
    (input.properties as Record<string, unknown> | undefined) ?? {},
  ).filter((name) => !seen.has(name));
  if (http.method !== "POST") {
    for (const name of remaining) {
      if (!queryCoercible(lookupInputProperty(input, name)))
        throw new Error(
          `${route}: ${http.method} query property ${name} must be string, number, integer or boolean`,
        );
    }
  }
}

export function validateRoutes(descriptors: readonly Descriptor[]): void {
  const seen = new Set<Route>();
  for (const descriptor of descriptors) {
    validateRoute(descriptor);
    if (seen.has(descriptor.route)) throw new Error(`${descriptor.route}: duplicate route`);
    seen.add(descriptor.route);
  }
}

export function matchRoute(
  descriptors: readonly Descriptor[],
  method: string,
  pathname: string,
): { descriptor: Descriptor; params: Record<string, string> } | undefined {
  for (const descriptor of descriptors) {
    const route = splitRoute(descriptor.route);
    if (route.method !== method) continue;
    const params = matchPath(route.path, pathname);
    if (params) return { descriptor, params };
  }
  return undefined;
}

export function buildInput(
  descriptor: Descriptor,
  params: Record<string, string>,
  query: URLSearchParams,
  body: unknown,
): { ok: true; input: Json } | { ok: false; error: ToolError } {
  const input: Record<string, Json> = { ...params };
  const taken = new Set(Object.keys(params));
  // A DELETE carries its arguments in the query, as a GET does: bodies on
  // DELETE are allowed by HTTP but dropped by some clients and proxies.
  if (splitRoute(descriptor.route).method !== "POST") {
    const keys = [...new Set(query.keys())];
    for (const key of keys) {
      if (taken.has(key))
        return {
          ok: false,
          error: { code: "invalid_input", message: `${key} is a path parameter`, retryable: false },
        };
      const values = query.getAll(key);
      if (values.length !== 1)
        return {
          ok: false,
          error: {
            code: "invalid_input",
            message: `Repeated query parameter ${key}`,
            retryable: false,
          },
        };
      const schema = lookupInputProperty(descriptor.input, key);
      if (!schema)
        return {
          ok: false,
          error: {
            code: "invalid_input",
            message: `Unknown query parameter ${key}`,
            retryable: false,
          },
        };
      const coerced = coerceQueryValue(schema, values[0]!);
      if (!coerced.ok)
        return {
          ok: false,
          error: { code: "invalid_input", message: coerced.message, retryable: false },
        };
      input[key] = coerced.value;
    }
    return { ok: true, input };
  }
  if (body !== undefined && (body === null || typeof body !== "object" || Array.isArray(body)))
    return {
      ok: false,
      error: { code: "invalid_input", message: "Expected a JSON object body", retryable: false },
    };
  for (const [key, value] of Object.entries((body ?? {}) as Record<string, unknown>)) {
    if (taken.has(key))
      return {
        ok: false,
        error: { code: "invalid_input", message: `${key} is a path parameter`, retryable: false },
      };
    input[key] = value as Json;
  }
  return { ok: true, input };
}

export function httpError(error: ToolError): { error: ToolError } {
  return { error };
}
